// "This household already has that policy number" — the warning that
// stops a second copy of a policy being created by hand.
//
// WHY IT EXISTS (Grant, 2026-08-18). Every hand-added policy in
// production was created between 2026-07-17 and 2026-08-17 — 119 of
// them. Five duplicated a policy the carrier import had already brought
// in, because the person adding it had no way to see it was already
// there. Joy found those five weeks later by scrolling household
// screens, and had to ask which copy to remove.
//
// So this is deliberately a WARNING WITH A NAME, not a block: it says
// WHICH record already holds the number and WHERE that record came
// from, because "you already have this" is only actionable if you can
// go look at the one you already have. "Add it anyway" stays one click
// away — a carrier genuinely can reuse a number across a rewrite, and
// refusing outright would trade a rare duplicate for a routine dead end.

import { Link } from "react-router-dom";

import { Button } from "../../ui";

export interface ExistingPolicyRef {
  service_record_id: string;
  insurance_policy_id: string;
  display_label: string | null;
  policy_number: string | null;
  policy_type: string | null;
  carrier_name: string | null;
  source: string | null;
  created_at: string | null;
}

/** Read the structured `existing` record off a 409 body, or null. */
export function readExistingPolicy(err: { data?: unknown }): ExistingPolicyRef | null {
  const body = err.data as { existing?: unknown } | null | undefined;
  const e = body?.existing as Partial<ExistingPolicyRef> | undefined;
  if (!e || typeof e.service_record_id !== "string") return null;
  return {
    service_record_id: e.service_record_id,
    insurance_policy_id: String(e.insurance_policy_id ?? ""),
    display_label: e.display_label ?? null,
    policy_number: e.policy_number ?? null,
    policy_type: e.policy_type ?? null,
    carrier_name: e.carrier_name ?? null,
    source: e.source ?? null,
    created_at: e.created_at ?? null,
  };
}

// `insurance_policies.source` in the words the team uses. An unmapped
// value shows as-is rather than being hidden — an unfamiliar source is
// information, and guessing at it would be worse than showing it.
function describeSource(source: string | null): string {
  const s = String(source ?? "").trim();
  if (!s) return "added here";
  if (s === "manual_entry") return "added by hand";
  if (s === "ivans_download") return "added from a carrier download";
  if (s === "ac_only") return "brought over from ActiveCampaign";
  if (s === "activecampaign") return "brought over from ActiveCampaign";
  if (s.startsWith("spreadsheet:")) {
    const carrier = s.slice("spreadsheet:".length).replace(/[_-]/g, " ");
    return `added from the ${carrier} carrier import`;
  }
  return `added by ${s}`;
}

function describeWhen(createdAt: string | null): string {
  const raw = String(createdAt ?? "").trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return ` on ${d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
}

export function DuplicatePolicyNumberWarning({
  message,
  existing,
  onAddAnyway,
  busy,
}: {
  message: string;
  existing: ExistingPolicyRef | null;
  onAddAnyway: () => void;
  busy?: boolean;
}) {
  return (
    <div
      style={{
        border: "1px solid var(--blue)",
        background: "rgba(74, 152, 198, 0.08)",
        borderRadius: "var(--radius-compact)",
        padding: "10px 12px",
        marginTop: 10,
        fontSize: 12.5,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 700, color: "var(--navy)" }}>{message}</div>
      {existing && (
        <div style={{ marginTop: 6 }}>
          The one already here:{" "}
          <Link
            to={`/service-records/${encodeURIComponent(existing.service_record_id)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 700 }}
          >
            {existing.display_label?.trim() || existing.carrier_name?.trim() || "this policy"}
          </Link>{" "}
          — {describeSource(existing.source)}
          {describeWhen(existing.created_at)}. Open it in a new tab and compare before adding a
          second.
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <Button
          variant="sw-default"
          onClick={onAddAnyway}
          disabled={busy}
          style={{ fontSize: 12, padding: "4px 10px" }}
        >
          {busy ? "Adding…" : "Add it anyway"}
        </Button>
        <span style={{ marginLeft: 8, color: "var(--fg-muted)", fontSize: 11.5 }}>
          Use this only if it really is a separate policy that happens to share a number.
        </span>
      </div>
    </div>
  );
}
